import { useEffect } from "react"
import { createPortal } from "react-dom"

import { IconPictureInPictureOn } from "@tabler/icons-react"
import { useSelector } from "@tanstack/react-store"
import { Store } from "@tanstack/store"

import { Button } from "@/components/ui/cn/button"
import { Tooltip } from "@/components/ui/cn/tooltip"
import { cn } from "@/lib/utils"
import { ObjectLoader } from "@/src/components/admin/siteAdmin/energy/Bills"
import { Document } from "@/src/graphql/public-types"

import { DraggablePIP } from "./DraggablePIP"

type Doc = Pick<Document, "pid" | "viewHref" | "contentType" | "fileName">

interface DocPreviewState {
  docs: Map<string, Doc>
  /** pid of the preview window raised above the others. */
  active: string | null
}

// Module-level so a preview button anywhere in the app can open a window in
// the single `DocsPreview` portal mounted at the router root.
const docPreviewStore = new Store(
  { docs: new Map(), active: null } as DocPreviewState,
  ({ setState }) => ({
    openDoc: (doc?: Doc | null) => {
      if (!doc) return
      setState((prev) => {
        const docs = new Map(prev.docs)
        docs.set(doc.pid, doc)
        return { docs, active: doc.pid }
      })
    },
    closeDoc: (doc: Doc) =>
      setState((prev) => {
        const docs = new Map(prev.docs)
        docs.delete(doc.pid)
        return { ...prev, docs }
      }),
    setActive: (doc: Doc | null) =>
      setState((prev) => ({ ...prev, active: doc ? doc.pid : null })),
    reset: () => setState(() => ({ docs: new Map(), active: null })),
  }),
)

export const DocPreviewButton = ({
  doc,
  className,
  onOpen,
}: {
  doc: Doc
  className?: string
  onOpen?: () => void
}) => {
  const unsupportedTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/octet-stream",
  ]
  const isUnsupported = unsupportedTypes.includes(doc.contentType)

  return (
    <Tooltip
      content={isUnsupported ? "Can't preview this file type" : "Open preview"}
      disableHoverablePopup
    >
      <Button
        aria-label="Open preview"
        icon={IconPictureInPictureOn}
        iconOnly
        variant="neutral"
        noBorder
        disabled={isUnsupported}
        className={className}
        onClick={(e) => {
          e.stopPropagation()
          docPreviewStore.actions.openDoc(doc)
          onOpen?.()
        }}
      />
    </Tooltip>
  )
}

export const DocsPreview = () => {
  const { docs, active } = useSelector(docPreviewStore)
  const { closeDoc, setActive, reset } = docPreviewStore.actions
  const docsArr = Array.from(docs.values())

  // Close every preview window when this portal unmounts.
  useEffect(() => reset, [reset])

  return createPortal(
    <div className="pointer-events-auto">
      {docsArr.map((doc) => (
        <DraggablePIP
          key={doc.viewHref}
          onClose={() => closeDoc(doc)}
          defaultSize={{ width: 600, height: 700 }}
          minHeight={400}
          minWidth={400}
          onClick={() => setActive(doc)}
          className={cn({ "z-[999]": active === doc.pid })}
        >
          <span className="p-2 text-center text-h3">File: {doc.fileName}</span>
          <div className="grow overflow-auto">
            <ObjectLoader
              className="h-full"
              width="100%"
              height="100%"
              type={doc.contentType}
              data={doc.viewHref}
            />
          </div>
        </DraggablePIP>
      ))}
    </div>,
    document.body,
  )
}
